import React, { useContext, useRef, useState } from "react";

import { my, register } from "@store/actions/user";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import instance from "@store/middleware/api";
import {
  closeCart,
  setCheckoutStep,
  setIsLoggedInCheckout,
} from "@store/reducers/cart";
import { resetUserState } from "@store/reducers/user";

import styled, { keyframes } from "styled-components";

import { ButtonLabel } from "@/components/ButtonGlyph";
import { Captcha, CAPTCHA_ENABLED } from "@/components/Captcha";
import PasswordRequirements from "@/components/PasswordRequirements";
import { useCaptcha } from "@/hooks/useCaptcha";
import { HandleOpenDialog } from "@/layouts/SecondRootLayout";
import { ERoutes } from "@/utils/constants";
import { setSession } from "@/utils/jwt";
import { firstPasswordProblem, isStrongPassword } from "@/utils/passwordPolicy";
import { currentAccessToken } from "@utils/viewAs";

import { BottomFade, ScrollArea, ScrollChevron, ScrollHint, useScrollAffordance } from "./phoneTemplate";
import {
  BackBtn,
  ButtonGroup,
  CheckboxBox,
  CheckboxLabel,
  CheckboxRow,
  ErrorText,
  IconCircle,
  InputField,
  PrimaryBtn,
  SectionLabel,
  SlidePanelBody,
  SlidePanelFooter,
  StepHeaderWrap,
  StepTitle,
} from "./styles";


const BagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#ebc28e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6V20C3 20.5304 3.21071 21.0391 3.58579 21.4142C3.96086 21.7893 4.46957 22 5 22H19C19.5304 22 20.0391 21.7893 20.4142 21.4142C20.7893 21.0391 21 20.5304 21 20V6L18 2H6Z" />
    <path d="M3 6H21" />
    <path d="M16 10C16 11.0609 15.5786 12.0783 14.8284 12.8284C14.0783 13.5786 13.0609 14 12 14C10.9391 14 9.92172 13.5786 9.17157 12.8284C8.42143 12.0783 8 11.0609 8 10" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 6L5 9L10 3" stroke="#3c2d23" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckoutSummary = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  color: #ebc28e;
  font-size: 1.3rem;
  font-weight: 600;
  text-align: center;
  margin-top: 4px;
`;




const ForgotLink = styled.a`
  color: #ffffff;
  font-size: 0.8rem;
  font-family: "Raleway", sans-serif;
  text-decoration: none;
  margin-left: auto;
  transition: all 0.3s ease;

  &:hover {
    text-decoration: underline;
  }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
`;

const ShakeWrap = styled.div<{ $shake: boolean }>`
  animation: ${({ $shake }) => ($shake ? shake : "none")} 0.5s ease;
`;


type CheckoutMode = "select" | "login";

const CheckoutView: React.FC = () => {
  const dispatch = useAppDispatch();
  const handleOpenDialog = useContext(HandleOpenDialog);
  const { isLoggedInCheckout, items, mode: cartMode } = useAppSelector((state) => state.cart);
  const user = useAppSelector((state) => state.user);

  // Determine if user is actually logged in from user reducer
  const isAuthenticated = !!(user as any)?.user?.email;

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const [mode, setMode] = useState<CheckoutMode>("select");

  // Reset mode when cart reopens. Authenticated users skip the CheckoutView
  // entirely (CartView routes around it), so we always start guests in "select".
  const { checkoutStep: currentStep } = useAppSelector((state) => state.cart);
  React.useEffect(() => {
    if (currentStep === "cart") {
      setMode("select");
    }
  }, [currentStep]);

  // Helper: after a successful login or signup mid-checkout, route the
  // freshly-authenticated user to the right next step. Subscription flow
  // jumps to plan-config; one-time flow jumps to tip (PaymentView captures
  // billing later).
  const advanceAfterAuth = React.useCallback(() => {
    if (cartMode === "subscription") {
      dispatch(setCheckoutStep("plan-config"));
    } else {
      dispatch(setCheckoutStep("tip"));
    }
  }, [cartMode, dispatch]);

  // Skip the Checkout step entirely for authenticated users — regardless of
  // entry point. CartView routes around it, but the subscription-page CTAs
  // (`openSubscriptionCheckout`) jump straight to "checkout" via the reducer,
  // so this auto-advance covers that path too.
  React.useEffect(() => {
    if (currentStep === "checkout" && (isAuthenticated || isLoggedInCheckout)) {
      advanceAfterAuth();
    }
  }, [currentStep, isAuthenticated, isLoggedInCheckout, advanceAfterAuth]);

  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupError, setSignupError] = useState("");
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupFieldErrors, setSignupFieldErrors] = useState<Record<string, boolean>>({});
  // Signup parity with the LoginDialog: real terms acceptance + newsletter + the
  // S-004 reCAPTCHA (was missing here → bypassed the captcha + auto-accepted terms).
  const [signupTermsChecked, setSignupTermsChecked] = useState(false);
  // Default OFF — newsletter consent must be an active opt-in (GDPR / CJEU Planet49).
  const [signupNewsletterChecked, setSignupNewsletterChecked] = useState(false);
  const [signupTermsInvalid, setSignupTermsInvalid] = useState(false);
  const [shakeSignupTerms, setShakeSignupTerms] = useState(false);
  const [captchaSolved, setCaptchaSolved] = useState(false);
  const [shakeSignupFields, setShakeSignupFields] = useState(false);
  const { captchaRef: signupCaptchaRef } = useCaptcha();
  const captchaRequired = CAPTCHA_ENABLED;
  // Scroll affordance for the checkout body (the signup form can overflow).
  const bodyRef = useRef<HTMLDivElement>(null);
  const canScrollDown = useScrollAffordance(bodyRef);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [shakeLogin, setShakeLogin] = useState(false);

  // No consent gate here any more — this view is a login/signup gate that
  // authenticated users never see, so gating on consent it does not collect
  // would have blocked guests only. ReviewView owns it now.
  const canContinue = true;

  const handleContinue = () => {

    if (cartMode === "subscription") {
      dispatch(setCheckoutStep("plan-config"));
    } else if (isAuthenticated || isLoggedInCheckout) {
      // Skip billing for logged in users, go to tip
      dispatch(setCheckoutStep("tip"));
    } else {
      // Guest users need billing
      dispatch(setCheckoutStep("billing"));
    }
  };



  const [loginLoading, setLoginLoading] = useState(false);

  const handleLogoutCheckout = async () => {
    try {
      const token = currentAccessToken();
      const refreshTokenStr = localStorage.getItem("refreshToken");
      await instance.post("/auth/logout", {
        accessToken: token ? JSON.parse(token).value : "",
        refreshToken: refreshTokenStr ? JSON.parse(refreshTokenStr).value : "",
      });
    } catch {
      // ignore logout API errors
    }
    const cart = localStorage.getItem("as-cart");
    localStorage.clear();
    if (cart) localStorage.setItem("as-cart", cart);
    dispatch(setIsLoggedInCheckout(false));
    dispatch(resetUserState());
    setMode("login");
  };

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) {
      setLoginError("Please fill in all fields");
      return;
    }
    setLoginLoading(true);
    setLoginError("");
    try {
      // Logout old session if switching accounts
      if (isAuthenticated) {
        await handleLogoutCheckout();
      }
      const resp = await instance.post("/auth/login", { email: loginEmail, password: loginPassword });
      const { accessToken, refreshToken, accessTokenExpiresAt, refreshTokenExpiresAt } = resp.data;
      setSession({
        accessToken,
        refreshToken,
        accessTokenSecondToExpiration: accessTokenExpiresAt,
        refreshTokenSecondToExpiration: refreshTokenExpiresAt,
      });
      await dispatch(my());
      dispatch(setIsLoggedInCheckout(true));
      advanceAfterAuth();
    } catch (err: any) {
      setLoginError(err?.message || "Invalid email or password");
      setShakeLogin(true);
      setTimeout(() => setShakeLogin(false), 500);
    } finally {
      setLoginLoading(false);
    }
  };

  const clearSignupFieldError = (field: string) => {
    setSignupFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      if (Object.keys(next).length === 0) setSignupError("");
      return next;
    });
  };

  const handleSignup = async () => {
    // Shake EVERY incomplete element at once (parity with the LoginDialog); no
    // generic "fill in all fields" line — the red borders + shake convey it.
    const errors: Record<string, boolean> = {};
    if (!signupName) errors.name = true;
    if (!signupEmail) errors.email = true;
    // Full policy — this form was on a 6-character rule while the signup
    // dialog was on 8 and the server on none. All three now share one source.
    const pwProblem = signupPassword ? firstPasswordProblem(signupPassword) : null;
    if (!signupPassword || pwProblem) errors.password = true;
    setSignupFieldErrors(errors);
    setSignupError(pwProblem ?? "");
    let incomplete = Object.keys(errors).length > 0;
    if (incomplete) {
      setShakeSignupFields(false);
      requestAnimationFrame(() => setShakeSignupFields(true));
    }
    if (!signupTermsChecked) {
      setSignupTermsInvalid(true);
      setShakeSignupTerms(false);
      requestAnimationFrame(() => setShakeSignupTerms(true));
      incomplete = true;
    }
    const captchaToken = (signupCaptchaRef.current?.getValue() as string) || undefined;
    if (captchaRequired && !captchaToken) {
      incomplete = true;
    }
    if (incomplete) return;
    setSignupLoading(true);
    setSignupError("");
    try {
      await dispatch(register({
        firstName: signupName,
        email: signupEmail,
        password: signupPassword,
        hasAcceptedTerms: signupTermsChecked,
        // One checkbox opts into the Newsletter (General) + Promotional; tune later on /account/notifications.
        hasGeneralNewsletter: signupNewsletterChecked,
        promoEmailsEnabled: signupNewsletterChecked,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        captchaToken,
      }) as any).unwrap();
      signupCaptchaRef.current?.reset();
      dispatch(setIsLoggedInCheckout(true));
      // NOTE: the verification code is sent by the VerifyEmail modal's mount
      // effect (SecondRootLayout auto-opens it for the now-unverified user) —
      // do NOT send it here too, or the user gets two codes (the first one
      // then invalidated). See git history: a sendVerificationCode() call here
      // double-sent.
      advanceAfterAuth();
    } catch (err: any) {
      signupCaptchaRef.current?.reset();
      setCaptchaSolved(false);
      setSignupError(err?.message || "Registration failed. Please try again.");
    } finally {
      setSignupLoading(false);
    }
  };

  const renderAccountSection = () => {
    if (mode === "login") {
      return (
        <ShakeWrap $shake={shakeLogin} style={{ marginBottom: 16, padding: "20px", background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(235, 194, 142, 0.15)", borderRadius: "8px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <InputField
              type="email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              placeholder="Email"
            />
            <InputField
              type="password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              placeholder="Password"
            />
            {loginError && <ErrorText>{loginError}</ErrorText>}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
              <ForgotLink
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  dispatch(closeCart());
                  setTimeout(() => {
                    if (handleOpenDialog) handleOpenDialog(true, true, false, 2);
                  }, 200);
                }}
              >
                Forgot password?
              </ForgotLink>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <BackBtn onClick={() => setMode("select")} style={{ flex: 1 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><polyline points="12 19 5 12 12 5" /></svg>
                {" "}Back
              </BackBtn>
              <PrimaryBtn onClick={handleLogin} disabled={loginLoading || !loginEmail || !loginPassword} style={{ flex: 2 }}>
                {loginLoading ? "Logging in..." : "Login"}
              </PrimaryBtn>
            </div>
            {!isAuthenticated && (
              <button
                onClick={() => setMode("select")}
                onMouseEnter={(e) => { (e.target as HTMLElement).style.textDecoration = "underline"; }}
                onMouseLeave={(e) => { (e.target as HTMLElement).style.textDecoration = "none"; }}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#9cd3fd", fontFamily: "'Raleway', sans-serif", fontSize: "0.8125rem", textAlign: "center", marginTop: "4px", width: "100%" }}
              >
                Create New Account
              </button>
            )}
          </div>
        </ShakeWrap>
      );
    }

    // Not authenticated — signup form. Authenticated users never reach this
    // view: CartView routes them straight to plan-config / tip / billing.
    return (
      <div style={{ marginBottom: 16, padding: "20px", background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(235, 194, 142, 0.15)", borderRadius: "8px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <span style={{ color: "#ffffff", fontSize: "0.9375rem", fontWeight: 500, fontFamily: "'Raleway', sans-serif", textAlign: "center" }}>Create an account to checkout</span>
          <ShakeWrap $shake={shakeSignupFields} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <InputField
              type="text"
              value={signupName}
              onChange={(e) => setSignupName(e.target.value)}
              onFocus={() => clearSignupFieldError("name")}
              onKeyDown={(e) => e.key === "Enter" && handleSignup()}
              placeholder="First name"
              $error={signupFieldErrors.name}
            />
            <InputField
              type="email"
              value={signupEmail}
              onChange={(e) => setSignupEmail(e.target.value)}
              onFocus={() => clearSignupFieldError("email")}
              onKeyDown={(e) => e.key === "Enter" && handleSignup()}
              placeholder="Email"
              $error={signupFieldErrors.email}
            />
            <InputField
              type="password"
              value={signupPassword}
              onChange={(e) => setSignupPassword(e.target.value)}
              onFocus={() => clearSignupFieldError("password")}
              onKeyDown={(e) => e.key === "Enter" && handleSignup()}
              placeholder="Password"
              // Red border while the password does not qualify, matching the
              // red rules below — as well as the post-submit field error.
              $error={
                signupFieldErrors.password ||
                (signupPassword.length > 0 && !isStrongPassword(signupPassword))
              }
            />
            <PasswordRequirements password={signupPassword} tone="error" />
          </ShakeWrap>
          {signupError && <ErrorText>{signupError}</ErrorText>}

          {/* Marketing opt-in first + highlighted; Terms second + plain (transparent 1px
              border keeps the box aligned with the highlighted row above). Wrapped in a
              tight-gap column so the two sit close together (not spread by the outer 14px
              flex gap + row margin). + S-004 captcha below. */}
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <CheckboxRow style={{ background: "rgba(235, 194, 142, 0.1)", border: "1px dashed rgba(235, 194, 142, 0.5)", marginBottom: 0 }}>
              <CheckboxBox $checked={signupNewsletterChecked} onClick={() => setSignupNewsletterChecked(!signupNewsletterChecked)} style={{ cursor: "pointer" }}>
                <CheckIcon />
              </CheckboxBox>
              <CheckboxLabel>Send me emails about new released music and offers.</CheckboxLabel>
            </CheckboxRow>

            <ShakeWrap
              $shake={shakeSignupTerms}
              style={{ borderRadius: 8, ...(signupTermsInvalid && !signupTermsChecked ? { boxShadow: "0 0 0 1px #e47a6a" } : {}) }}
            >
              <CheckboxRow style={{ background: "transparent", border: "1px solid transparent", marginBottom: 0 }}>
                <CheckboxBox
                  $checked={signupTermsChecked}
                  onClick={() => { setSignupTermsChecked(!signupTermsChecked); setSignupTermsInvalid(false); }}
                  style={{ cursor: "pointer" }}
                >
                  <CheckIcon />
                </CheckboxBox>
                <CheckboxLabel>
                  I have read and agree to the <a href={ERoutes.Conditions} style={{ color: "#9cd3fd" }}>Terms of Use</a>,{" "}
                  <a href="/privacy" style={{ color: "#9cd3fd" }}>Privacy Policy</a>,{" "}
                  <a href={ERoutes.LicenceTerms} target="_blank" rel="noopener noreferrer" style={{ color: "#9cd3fd" }}>YouTube Licence Terms</a> and{" "}
                  <a href="/refund-policy" style={{ color: "#9cd3fd" }}>Refund Policy</a>.
                </CheckboxLabel>
              </CheckboxRow>
            </ShakeWrap>
          </div>

          {/* Invisible captcha: auto-solves in the background and takes no space. */}
          <Captcha hideUntilNeeded onChange={(token) => setCaptchaSolved(!!token)} />

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <PrimaryBtn
              $dimmed={!(signupName && signupEmail && isStrongPassword(signupPassword) && signupTermsChecked && (captchaSolved || !captchaRequired))}
              onClick={handleSignup}
              disabled={signupLoading}
              style={{ flex: 1 }}
            >
              {signupLoading ? "Creating account..." : "Create Account"}
            </PrimaryBtn>
          </div>
          <button
            onClick={() => setMode("login")}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#9cd3fd", fontFamily: "'Raleway', sans-serif", fontSize: "0.8125rem", textAlign: "center" }}
          >
            Already have an account? Log in
          </button>
        </div>
      </div>
    );
  };

  // Authenticated users never see this view — render nothing while the
  // auto-advance effect transitions them to the next step.
  if (isAuthenticated || isLoggedInCheckout) return null;

  return (
    <>
      <ScrollArea>
      <SlidePanelBody ref={bodyRef}>
      <StepHeaderWrap>
        <IconCircle>
          <BagIcon />
        </IconCircle>
        <StepTitle style={{ marginBottom: 0 }}>Checkout</StepTitle>
        <CheckoutSummary>{cartMode === "subscription" ? "Subscription" : `${itemCount} item${itemCount !== 1 ? 's' : ''} · $${subtotal.toFixed(2)}`}</CheckoutSummary>
      </StepHeaderWrap>

      <SectionLabel>
        <span>Account</span>
      </SectionLabel>

      {renderAccountSection()}

      {/* Consent used to live here. It moved to ReviewView, because this view
          is ONLY rendered for guests — CartView routes authenticated users
          straight past it (it is a login/signup gate now), so boxes here were
          never shown to a logged-in buyer while PaymentView posted `true` for
          them anyway. Review is the one step every one-time buyer passes
          through. Do not add consent back here; it would ask guests twice. */}

      </SlidePanelBody>
      <BottomFade $visible={canScrollDown} aria-hidden="true"><ScrollHint><span>Scroll</span><ScrollChevron /></ScrollHint></BottomFade>
      </ScrollArea>

      {(isAuthenticated || isLoggedInCheckout) && mode !== "login" && (
        <SlidePanelFooter>
          <ButtonGroup style={{ marginTop: 0, borderTop: "none", paddingTop: 0 }}>
            <PrimaryBtn $dimmed={!canContinue} onClick={handleContinue} style={{ flex: 1 }}>
              <ButtonLabel glyph="arrowRight">Continue</ButtonLabel>
            </PrimaryBtn>
          </ButtonGroup>
        </SlidePanelFooter>
      )}
    </>
  );
};

export default CheckoutView;
